<template>
  <div class="container">
    <!--b-card :title="$t('monthly_expense_management')" :class="isRtleft ? 'text-end' : 'text-start'"-->
    <h2>{{ $t('titre.MonthlyExpenseManager') }}</h2>

    <!-- Month & Year Selection -->
    <div class="d-flex mb-3">
      <select v-model="selectedYear" class="form-control me-2" @change="fetchExpenses">
        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
      </select>
      <select v-model="selectedMonth" class="form-control" @change="fetchExpenses">
        <option value="all">{{ $t('all_months') }}</option>
        <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
      </select>
    </div>

    <!-- Expense Form -->
    <div>
      <form @submit.prevent="saveExpense" class="row g-3 align-items-center justify-content-between">

        <div class="form-group row">
          <label for="selectExpense" class="col-sm-2 text-start col-form-label">{{ $t('select_expense') }}</label>
          <div class="col-sm-10">
            <select id="selectExpense" v-model="formData.expense.id" class="form-control" required>
              <option value="" disabled>{{ $t('select_expense') }}</option>
              <option v-for="expense in expenseOptions" :key="expense.id" :value="expense.id">
                {{ expense.value }}
              </option>
            </select>
          </div>

          <label for="inputAmount" class="col-sm-2 text-start col-form-label">{{ $t('input_amount') }}</label>
          <div class="col-sm-4">
            <div class="input-group">
              <input id="inputAmount" v-model="formData.amount" type="number" class="form-control"
                :placeholder="$t('input_amount')" required step="0.01" @blur="formatAmount" @input="parseAmount" />
              <span class="input-group-text">€</span>
            </div>
          </div>

          <label for="year" class="col-sm-1 text-start col-form-label">{{ $t('year') }}</label>
          <div class="col-sm-1">
            <select id="year" v-model="formData.year" class="form-control" required>
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>

          <label for="month" class="col-sm-1 text-start col-form-label">{{ $t('month') }}</label>
          <div class="col-sm-3">
            <select id="month" v-model="formData.month" class="form-control" required>
              <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
            </select>
          </div>
        </div>
        <div class="btn-update-cancel">
          <button class="btn btn-outline-success btn-sm me-2 btn-lg w-80" type="submit">
            <i class="fas fa-save"></i> {{ formData.id ? $t('update') : $t('add') }}
          </button>
          <button class="btn btn-outline-danger btn-sm me-2 btn-lg w-80" type="reset" @click="resetForm">
            <i class="bi bi-x"></i> {{ $t('cancel') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Table with Pagination -->
    <div class="table-responsive">
      <table class="custom-table">
        <thead>
          <tr>
            <th>{{ $t('expense') }}</th>
            <th>{{ $t('amount') }}</th>
            <th>{{ $t('year') }}</th>
            <th>{{ $t('month') }}</th>
            <th>{{ $t('actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in paginatedExpenses" :key="index">
            <td>{{ item.expense.value }}</td>
            <td>{{ formatPrice(item.amount) }}</td>
            <td>{{ item.year }}</td>
            <td>{{ months[item.month - 1] }}</td>
            <td>
              <div class="btn-edit-delete">
                <button class="btn btn-outline-primary btn-sm me-2" @click="editExpense(item)">
                  <i class="fas fa-edit"></i> <!--{{ $t('edit') }}-->
                </button>
                <button class="btn btn-outline-danger btn-sm" @click="deleteExpense(item.id)">
                  <i class="fas fa-trash"></i> <!--{{ $t('delete') }}-->
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td><strong>{{ $t('total') }}</strong></td>
            <td><strong>{{ formatPrice(totalAmount) }}</strong></td>
            <td colspan="3"></td>
          </tr>
        </tfoot>
      </table>

      <!-- Pagination Controls -->
      <div class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1">❮</button>
        <span>{{ currentPage }} / {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage === totalPages">❯</button>
      </div>
    </div>
  </div>
</template>

<script>
import { MonthlyExpensesService } from "../api";
import { ExpenseService } from "../api";
import { Message } from "../libjs/alertService";

export default {
  data() {
    return {
      expenses: [],
      expenseOptions: [],
      selectedYear: new Date().getFullYear(),
      selectedMonth: "all",
      formData: {
        id: null,
        expense: { id: "" },
        amount: "",
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      },
      months: [
        this.$t("months.january"), this.$t("months.february"), this.$t("months.march"), this.$t("months.april"),
        this.$t("months.may"), this.$t("months.june"), this.$t("months.july"), this.$t("months.august"),
        this.$t("months.september"), this.$t("months.october"), this.$t("months.november"), this.$t("months.december")
      ],
      availableYears: Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i),
      currentPage: 1,
      pageSize: 10, // Show 10 rows per page
    };
  },
  computed: {
    filteredExpenses() {
      if (this.selectedMonth === "all") {
        return this.expenses.filter(item => item.year == this.selectedYear);
      }
      return this.expenses.filter(item =>
        item.year == this.selectedYear && item.month === this.selectedMonth
      );
    },
    totalAmount() {
      return this.filteredExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    },
    isRtleft() {
      return this.$i18n.locale === "ar";
    },
    formattedAmount: {
      get() {
        return this.formatPrice(this.formData.amount);
      },
      set(value) {
        this.formData.amount = this.parsePrice(value);
      }
    },
    totalPages() {
      return Math.ceil(this.filteredExpenses.length / this.pageSize);
    },
    paginatedExpenses() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredExpenses.slice(start, end);
    },
  },
  methods: {
    async fetchExpenses() {
      try {
        const response = await MonthlyExpensesService.listMonthlyExpense();
        this.expenses = response;
      } catch (error) {
        console.error("Error fetching expenses:", error);
      }
    },
    async fetchExpenseOptions() {
      try {
        const response = await ExpenseService.listExpense();
        this.expenseOptions = response;
      } catch (error) {
        console.error("Error fetching expense categories:", error);
      }
    },
    /*async saveExpense() {
      try {
        if (this.formData.id) {
          await MonthlyExpensesService.updateMonthlyExpense(this.formData.id, this.formData);
        } else {
          await MonthlyExpensesService.createMonthlyExpense(this.formData);
        }
        this.resetForm();
        this.fetchExpenses();
      } catch (error) {
        Message("danger", this.$t("message.operation_failed"));
      }
    },*/

    async saveExpense() {
      try {
        if (this.formData.id) {
          await MonthlyExpensesService.updateMonthlyExpense(this.formData.id, this.formData);
          Message("success", this.$t("message.update_success"));
        } else {
          console.log('this.formData ' + JSON.stringify(this.formData))
          await MonthlyExpensesService.createMonthlyExpense(this.formData);
          Message("success", this.$t("message.add_success"));
        }
        this.resetForm();
        this.fetchExpenses();
      } catch (error) {
        Message("danger", this.$t("message.operation_failed"));
      }
    },
    async deleteExpense(id) {
      try {
        await MonthlyExpensesService.deleteMonthlyExpense(id);
        this.fetchExpenses();
        Message("success", this.$t("message.delete_success"));
      } catch (error) {
        Message("danger", this.$t("message.delete_failed"));
      }
    },
    editExpense(expense) {
      this.formData = { ...expense };
    },
    resetForm() {
      this.formData = { id: null, expense: { id: "" }, amount: "", year: this.selectedYear, month: this.selectedMonth };
    },
    formatPrice(value) {
      if (!value) return "0 €";
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2
      }).format(value);
    },
    // Convert input back to a number
    parsePrice(value) {
      let rawValue = value.replace(/[^\d,.-]/g, "").replace(",", ".");
      return parseFloat(rawValue) || 0;
    },

    // Ensure formatting after input
    formatAmount(event) {
      console.log('formatAmount---')
      this.formData.amount = this.parsePrice(event.target.value);
    },
    parseAmount(event) {
      console.log('parseAmount-1- ' + event.target.value)
      let value = event.target.value.replace(/[^0-9,.-]/g, "").replace(",", ".");
      if (!isNaN(value) && value !== "") {
        this.formData.amount = parseFloat(value);
      }
      console.log('parseAmount-2- ' + event.target.value)
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
  },
  mounted() {
    this.fetchExpenses();
    this.fetchExpenseOptions();
  },
};
</script>

<style scoped>
.container {
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 0px;
  border-radius: 10px;
}

h2 {
  background-color: #47c399;
  border-radius: 10px 10px 0 0;
  color: azure;
  padding: 3px;
  text-align: justify;
  padding-left: 15px;
}

/* Shared Styles */
body,
input,
button {
  font-family: "Arial", sans-serif;
  font-size: 14px;
}

/* Form Styles */
form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 15px;
  margin: 1px;
}

form input,
form select {
  flex: 1;
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.input-group-text {
  font-size: 14px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
}

form button {
  padding: 6px 12px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
}

/* Table Styles */
.custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-top: 20px;
}

.custom-table thead tr {
  background-color: #f2f2f2;
}

.custom-table th,
.custom-table td {
  border: 1px solid #ddd;
  padding: 6px;
  text-align: center;
}

/* Footer Row Styling */
.custom-table tfoot tr {
  background-color: #f2f2f2;
}

.custom-table tfoot td {
  font-weight: bold;
  text-align: right;
}

/* Filter Input Styling */
.filter-input {
  width: 100%;
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

/* Remove arrows in Chrome, Safari, Edge, and Opera */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Remove arrows in Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
}

.pagination button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 5px 10px;
  margin: 0 5px;
  border-radius: 5px;
  cursor: pointer;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination span {
  font-size: 16px;
  font-weight: bold;
}
</style>